import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Paper,
} from "@mui/material";
import {
  ContentCopy,
  OpenInNew,
  CheckCircle,
  Dns,
  Close,
} from "@mui/icons-material";
import { getResponsiveDialogProps } from "../../theme/responsive";

const DnsSetupGuideDialog = ({
  open,
  onClose,
  serverIp,
  customDomain,
  cnameTarget,
}) => {
  const [activeProvider, setActiveProvider] = useState(0);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Use CNAME-based instructions when cnameTarget is available
  const useCname = !!cnameTarget;

  const providers = [
    {
      name: "GoDaddy",
      steps: [
        {
          label: "Log in to GoDaddy",
          content: (
            <Typography variant="body2">
              Go to{" "}
              <Typography
                component="span"
                color="primary"
                sx={{ cursor: "pointer" }}
                onClick={() =>
                  window.open(
                    "https://dcc.godaddy.com/domains",
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
              >
                dcc.godaddy.com/domains
                <OpenInNew
                  sx={{ fontSize: 14, ml: 0.5, verticalAlign: "middle" }}
                />
              </Typography>{" "}
              and sign in to your account.
            </Typography>
          ),
        },
        {
          label: "Select your domain",
          content: (
            <Typography variant="body2">
              Find <strong>{customDomain || "your domain"}</strong> in your
              domain list and click on it to open domain settings.
            </Typography>
          ),
        },
        {
          label: "Navigate to DNS Management",
          content: (
            <Typography variant="body2">
              Click on the <strong>"DNS"</strong> tab or{" "}
              <strong>"Manage DNS"</strong> button to access DNS records.
            </Typography>
          ),
        },
        ...(useCname
          ? [
              {
                label: "Add CNAME Record for www",
                content: (
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Click <strong>"Add New Record"</strong> and enter:
                    </Typography>
                    <DnsRecordDisplay
                      type="CNAME"
                      host="www"
                      value={cnameTarget}
                      ttl="600"
                      onCopy={copyToClipboard}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      If a CNAME or A record for www already exists, edit or
                      delete it first.
                    </Typography>
                  </Box>
                ),
              },
              {
                label: "Add A Record for root domain (@)",
                content: (
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Add an A record for the root domain (CNAME cannot be used
                      on the root/apex domain):
                    </Typography>
                    <DnsRecordDisplay
                      type="A"
                      host="@"
                      value={serverIp}
                      ttl="600"
                      onCopy={copyToClipboard}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      If an A record for @ already exists, edit it instead of
                      creating a new one.
                    </Typography>
                  </Box>
                ),
              },
            ]
          : [
              {
                label: "Add A Record for root domain (@)",
                content: (
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Click <strong>"Add New Record"</strong> and enter:
                    </Typography>
                    <DnsRecordDisplay
                      type="A"
                      host="@"
                      value={serverIp}
                      ttl="600"
                      onCopy={copyToClipboard}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      If an A record for @ already exists, edit it instead of
                      creating a new one.
                    </Typography>
                  </Box>
                ),
              },
              {
                label: "Add A Record for www",
                content: (
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Add another record:
                    </Typography>
                    <DnsRecordDisplay
                      type="A"
                      host="www"
                      value={serverIp}
                      ttl="600"
                      onCopy={copyToClipboard}
                    />
                  </Box>
                ),
              },
            ]),
        {
          label: "Save and verify",
          content: (
            <Typography variant="body2">
              Click <strong>"Save"</strong> for each record. DNS changes
              typically take <strong>15-30 minutes</strong> with GoDaddy, but
              can take up to 48 hours for full global propagation. Return to WP
              Spark and click <strong>"Verify DNS"</strong> to check the
              configuration.
            </Typography>
          ),
        },
      ],
    },
    {
      name: "Namecheap",
      steps: [
        {
          label: "Log in to Namecheap",
          content: (
            <Typography variant="body2">
              Go to{" "}
              <Typography
                component="span"
                color="primary"
                sx={{ cursor: "pointer" }}
                onClick={() =>
                  window.open(
                    "https://ap.www.namecheap.com/domains/list/",
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
              >
                ap.www.namecheap.com
                <OpenInNew
                  sx={{ fontSize: 14, ml: 0.5, verticalAlign: "middle" }}
                />
              </Typography>{" "}
              and sign in.
            </Typography>
          ),
        },
        {
          label: "Go to Domain List",
          content: (
            <Typography variant="body2">
              Click on <strong>"Domain List"</strong> in the left sidebar, then
              click <strong>"Manage"</strong> next to{" "}
              <strong>{customDomain || "your domain"}</strong>.
            </Typography>
          ),
        },
        {
          label: "Open Advanced DNS",
          content: (
            <Typography variant="body2">
              Click the <strong>"Advanced DNS"</strong> tab at the top of the
              domain management page.
            </Typography>
          ),
        },
        ...(useCname
          ? [
              {
                label: "Add CNAME Record for www",
                content: (
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Click <strong>"Add New Record"</strong>:
                    </Typography>
                    <DnsRecordDisplay
                      type="CNAME"
                      host="www"
                      value={cnameTarget}
                      ttl="Automatic"
                      onCopy={copyToClipboard}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Delete any existing CNAME or A records for www before
                      adding this one.
                    </Typography>
                  </Box>
                ),
              },
              {
                label: "Add A Record for root domain (@)",
                content: (
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Add an A record for the root domain:
                    </Typography>
                    <DnsRecordDisplay
                      type="A"
                      host="@"
                      value={serverIp}
                      ttl="Automatic"
                      onCopy={copyToClipboard}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Delete any existing A records for @ that point to a
                      different IP.
                    </Typography>
                  </Box>
                ),
              },
            ]
          : [
              {
                label: "Add A Record for root domain (@)",
                content: (
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Click <strong>"Add New Record"</strong>:
                    </Typography>
                    <DnsRecordDisplay
                      type="A"
                      host="@"
                      value={serverIp}
                      ttl="Automatic"
                      onCopy={copyToClipboard}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Delete any existing A records for @ (like parking page
                      records) before adding this one.
                    </Typography>
                  </Box>
                ),
              },
              {
                label: "Add A Record for www",
                content: (
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Add another record:
                    </Typography>
                    <DnsRecordDisplay
                      type="A"
                      host="www"
                      value={serverIp}
                      ttl="Automatic"
                      onCopy={copyToClipboard}
                    />
                  </Box>
                ),
              },
            ]),
        {
          label: "Save and verify",
          content: (
            <Typography variant="body2">
              Click the <strong>green checkmark</strong> to save each record.
              Namecheap DNS typically propagates in{" "}
              <strong>15-30 minutes</strong>. Return to WP Spark and click{" "}
              <strong>"Verify DNS"</strong>.
            </Typography>
          ),
        },
      ],
    },
    {
      name: "Hostinger",
      steps: [
        {
          label: "Log in to Hostinger",
          content: (
            <Typography variant="body2">
              Go to{" "}
              <Typography
                component="span"
                color="primary"
                sx={{ cursor: "pointer" }}
                onClick={() =>
                  window.open(
                    "https://hpanel.hostinger.com/domains",
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
              >
                hpanel.hostinger.com
                <OpenInNew
                  sx={{ fontSize: 14, ml: 0.5, verticalAlign: "middle" }}
                />
              </Typography>{" "}
              and sign in to your account.
            </Typography>
          ),
        },
        {
          label: "Select your domain",
          content: (
            <Typography variant="body2">
              Navigate to <strong>"Domains"</strong> section, find{" "}
              <strong>{customDomain || "your domain"}</strong>, and click{" "}
              <strong>"Manage"</strong>.
            </Typography>
          ),
        },
        {
          label: "Open DNS / Nameservers",
          content: (
            <Typography variant="body2">
              Click on <strong>"DNS / Nameservers"</strong> in the left sidebar.
              Make sure you are using <strong>Hostinger nameservers</strong>{" "}
              (ns1.dns-parking.com, ns2.dns-parking.com) or your own custom
              nameservers.
            </Typography>
          ),
        },
        ...(useCname
          ? [
              {
                label: "Add CNAME Record for www",
                content: (
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      In the DNS Records section, click{" "}
                      <strong>"Add Record"</strong>:
                    </Typography>
                    <DnsRecordDisplay
                      type="CNAME"
                      host="www"
                      value={cnameTarget}
                      ttl="14400"
                      onCopy={copyToClipboard}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Remove any existing CNAME or A records for www.
                    </Typography>
                  </Box>
                ),
              },
              {
                label: "Add A Record for root domain (@)",
                content: (
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Add an A record for the root domain:
                    </Typography>
                    <DnsRecordDisplay
                      type="A"
                      host="@"
                      value={serverIp}
                      ttl="14400"
                      onCopy={copyToClipboard}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Remove any existing A records for @ that point to a
                      different IP.
                    </Typography>
                  </Box>
                ),
              },
            ]
          : [
              {
                label: "Add A Record for root domain (@)",
                content: (
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      In the DNS Records section, click{" "}
                      <strong>"Add Record"</strong>:
                    </Typography>
                    <DnsRecordDisplay
                      type="A"
                      host="@"
                      value={serverIp}
                      ttl="14400"
                      onCopy={copyToClipboard}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Remove any existing A records for @ that point to a
                      different IP.
                    </Typography>
                  </Box>
                ),
              },
              {
                label: "Add A Record for www",
                content: (
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Add another record:
                    </Typography>
                    <DnsRecordDisplay
                      type="A"
                      host="www"
                      value={serverIp}
                      ttl="14400"
                      onCopy={copyToClipboard}
                    />
                  </Box>
                ),
              },
            ]),
        {
          label: "Save and verify",
          content: (
            <Typography variant="body2">
              Click <strong>"Add Record"</strong> to save. Hostinger DNS usually
              propagates in <strong>15 minutes to 1 hour</strong>. Return to WP
              Spark and click <strong>"Verify DNS"</strong>.
            </Typography>
          ),
        },
      ],
    },
    {
      name: "Cloudflare",
      steps: [
        {
          label: "Log in to Cloudflare",
          content: (
            <Typography variant="body2">
              Go to{" "}
              <Typography
                component="span"
                color="primary"
                sx={{ cursor: "pointer" }}
                onClick={() =>
                  window.open(
                    "https://dash.cloudflare.com/",
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
              >
                dash.cloudflare.com
                <OpenInNew
                  sx={{ fontSize: 14, ml: 0.5, verticalAlign: "middle" }}
                />
              </Typography>{" "}
              and sign in to your account.
            </Typography>
          ),
        },
        {
          label: "Select your domain",
          content: (
            <Typography variant="body2">
              Click on <strong>{customDomain || "your domain"}</strong> from
              your sites list to open the domain dashboard.
            </Typography>
          ),
        },
        {
          label: "Go to DNS settings",
          content: (
            <Typography variant="body2">
              Click <strong>"DNS"</strong> in the left sidebar, then click{" "}
              <strong>"Records"</strong>.
            </Typography>
          ),
        },
        ...(useCname
          ? [
              {
                label: "Add CNAME Record for root domain (@)",
                content: (
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Cloudflare supports <strong>CNAME flattening</strong> at
                      the root domain — so you can use CNAME for both @ and www:
                    </Typography>
                    <DnsRecordDisplay
                      type="CNAME"
                      host="@"
                      value={cnameTarget}
                      ttl="Auto"
                      onCopy={copyToClipboard}
                    />
                    <Alert severity="info" sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        Cloudflare automatically flattens CNAME records at the
                        root level, so this works even though standard DNS
                        doesn't allow CNAME on apex domains. Set{" "}
                        <strong>Proxy status to "DNS only" (grey cloud)</strong>{" "}
                        initially.
                      </Typography>
                    </Alert>
                  </Box>
                ),
              },
              {
                label: "Add CNAME Record for www",
                content: (
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Add another CNAME record:
                    </Typography>
                    <DnsRecordDisplay
                      type="CNAME"
                      host="www"
                      value={cnameTarget}
                      ttl="Auto"
                      onCopy={copyToClipboard}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Set{" "}
                      <strong>Proxy status to "DNS only" (grey cloud)</strong>{" "}
                      until DNS is verified. You can enable the orange cloud
                      proxy afterwards.
                    </Typography>
                  </Box>
                ),
              },
            ]
          : [
              {
                label: "Add A Record for root domain (@)",
                content: (
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Click <strong>"Add Record"</strong>:
                    </Typography>
                    <DnsRecordDisplay
                      type="A"
                      host="@"
                      value={serverIp}
                      ttl="Auto"
                      onCopy={copyToClipboard}
                    />
                  </Box>
                ),
              },
              {
                label: "Add A Record for www",
                content: (
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Add another record:
                    </Typography>
                    <DnsRecordDisplay
                      type="A"
                      host="www"
                      value={serverIp}
                      ttl="Auto"
                      onCopy={copyToClipboard}
                    />
                  </Box>
                ),
              },
            ]),
        {
          label: "Save and verify",
          content: (
            <Typography variant="body2">
              Click <strong>"Save"</strong>. Cloudflare DNS propagates almost
              instantly. Return to WP Spark and click{" "}
              <strong>"Verify DNS"</strong>.
            </Typography>
          ),
        },
      ],
    },
    {
      name: "Other Providers",
      steps: [
        {
          label: "Find DNS management",
          content: (
            <Typography variant="body2">
              Log in to your domain registrar or DNS provider. Look for{" "}
              <strong>"DNS Management"</strong>, <strong>"DNS Settings"</strong>
              , <strong>"Zone Editor"</strong>, or{" "}
              <strong>"DNS Records"</strong> in your control panel.
            </Typography>
          ),
        },
        ...(useCname
          ? [
              {
                label: "Add CNAME Record for www subdomain",
                content: (
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Create a CNAME record:
                    </Typography>
                    <DnsRecordDisplay
                      type="CNAME"
                      host="www"
                      value={cnameTarget}
                      ttl="3600"
                      onCopy={copyToClipboard}
                    />
                  </Box>
                ),
              },
              {
                label: "Add A Record for root domain",
                content: (
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Standard DNS does not support CNAME on the root domain, so
                      an A record is needed:
                    </Typography>
                    <DnsRecordDisplay
                      type="A"
                      host="@ (or leave empty)"
                      value={serverIp}
                      ttl="3600"
                      onCopy={copyToClipboard}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      If your DNS provider supports <strong>ALIAS</strong> or{" "}
                      <strong>ANAME</strong> records, you can use that instead
                      of an A record — set it to <strong>{cnameTarget}</strong>.
                    </Typography>
                  </Box>
                ),
              },
            ]
          : [
              {
                label: "Add A Record for root domain",
                content: (
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Create an A record with these values:
                    </Typography>
                    <DnsRecordDisplay
                      type="A"
                      host="@ (or leave empty)"
                      value={serverIp}
                      ttl="3600"
                      onCopy={copyToClipboard}
                    />
                  </Box>
                ),
              },
              {
                label: "Add A Record for www subdomain",
                content: (
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Create another A record:
                    </Typography>
                    <DnsRecordDisplay
                      type="A"
                      host="www"
                      value={serverIp}
                      ttl="3600"
                      onCopy={copyToClipboard}
                    />
                  </Box>
                ),
              },
            ]),
        {
          label: "Remove conflicting records",
          content: (
            <Typography variant="body2">
              Delete any existing A, AAAA, or CNAME records for{" "}
              <strong>@</strong> and <strong>www</strong> that point to
              different values. Keep MX, TXT, and other records as they are
              (email, SPF, etc. will continue working).
            </Typography>
          ),
        },
        {
          label: "Wait for propagation and verify",
          content: (
            <Typography variant="body2">
              DNS changes can take <strong>up to 48 hours</strong> to propagate
              globally, though most providers update within 15-60 minutes.
              Return to WP Spark and click <strong>"Verify DNS"</strong> to
              confirm your setup.
            </Typography>
          ),
        },
      ],
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      {...getResponsiveDialogProps("large")}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Dns color="primary" />
            <Typography variant="h6">DNS Setup Guide</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Summary */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            What you need to do:
          </Typography>
          <Typography variant="body2">
            Point your domain{" "}
            <strong>{customDomain || "yourdomain.com"}</strong> to our server by
            adding DNS records at your domain provider. This tells the internet
            to send traffic for your domain to our hosting platform.
          </Typography>
        </Alert>

        {/* Quick Reference */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: "action.hover" }}>
          <Typography variant="subtitle2" gutterBottom>
            Required DNS Records
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {useCname ? (
              <>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip label="CNAME" size="small" color="primary" />
                  <Typography variant="body2" fontFamily="monospace">
                    www → {cnameTarget}
                  </Typography>
                  {cnameTarget && (
                    <Tooltip title="Copy CNAME target">
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(cnameTarget)}
                      >
                        <ContentCopy sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Chip
                    label="Required"
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip label="A" size="small" color="secondary" />
                  <Typography variant="body2" fontFamily="monospace">
                    @ → {serverIp || "YOUR_SERVER_IP"}
                  </Typography>
                  {serverIp && (
                    <Tooltip title="Copy IP">
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(serverIp)}
                      >
                        <ContentCopy sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Chip
                    label="Required (root domain)"
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip label="A" size="small" color="primary" />
                  <Typography variant="body2" fontFamily="monospace">
                    @ → {serverIp || "YOUR_SERVER_IP"}
                  </Typography>
                  {serverIp && (
                    <Tooltip title="Copy IP">
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(serverIp)}
                      >
                        <ContentCopy sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Chip
                    label="Required"
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip label="A" size="small" color="primary" />
                  <Typography variant="body2" fontFamily="monospace">
                    www → {serverIp || "YOUR_SERVER_IP"}
                  </Typography>
                  <Chip
                    label="Recommended"
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                </Box>
              </>
            )}
          </Box>
          {useCname && (
            <Alert severity="info" sx={{ mt: 1.5, py: 0.5 }}>
              <Typography variant="body2">
                Using CNAME means you won't need to update your DNS if our
                server IP changes. Only the root domain (@) requires an A record
                because standard DNS doesn't allow CNAME on apex domains.
              </Typography>
            </Alert>
          )}
        </Paper>

        <Divider sx={{ mb: 2 }} />

        {/* Provider-specific guides */}
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Step-by-Step Guide
        </Typography>

        <Tabs
          value={activeProvider}
          onChange={(_, v) => setActiveProvider(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
        >
          {providers.map((provider, idx) => (
            <Tab key={idx} label={provider.name} />
          ))}
        </Tabs>

        <Stepper
          orientation="vertical"
          activeStep={-1}
          sx={{
            "& .MuiStepLabel-root": { cursor: "default" },
            "& .MuiStepIcon-root": { color: "primary.main" },
          }}
        >
          {providers[activeProvider].steps.map((step, idx) => (
            <Step key={idx} active expanded>
              <StepLabel>
                <Typography variant="subtitle2">{step.label}</Typography>
              </StepLabel>
              <StepContent>{step.content}</StepContent>
            </Step>
          ))}
        </Stepper>

        <Divider sx={{ my: 3 }} />

        {/* Important Notes */}
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Important Notes
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            <li>
              <Typography variant="body2">
                <strong>Do NOT change your nameservers</strong> — only add/edit
                the {useCname ? "CNAME and A records" : "A records"}. Changing
                nameservers will break email and other services.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Keep your MX records</strong> — email records (MX, SPF,
                DKIM) are separate and should not be modified.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>SSL is automatic</strong> — once DNS is verified, our
                platform automatically provisions a free SSL certificate via
                Let's Encrypt.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Propagation time varies</strong> — most providers update
                in 15-30 minutes, but full global propagation can take up to 48
                hours.
              </Typography>
            </li>
            {useCname && (
              <li>
                <Typography variant="body2">
                  <strong>Why CNAME + A record?</strong> — The www subdomain
                  uses a CNAME (infrastructure-independent), while the root
                  domain requires an A record because standard DNS doesn't
                  support CNAME on apex domains. Cloudflare users can use CNAME
                  for both.
                </Typography>
              </li>
            )}
          </Box>
        </Alert>

        {/* Troubleshooting */}
        <Alert severity="info">
          <Typography variant="subtitle2" gutterBottom>
            Troubleshooting
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            <li>
              <Typography variant="body2">
                <strong>Verification fails?</strong> Wait 15-30 minutes and try
                again. DNS propagation takes time.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Using Cloudflare?</strong> Set the proxy status to{" "}
                <strong>"DNS only" (grey cloud)</strong> initially. You can
                enable the orange cloud proxy after verification.
                {useCname &&
                  " Cloudflare also supports CNAME at the root domain via CNAME flattening."}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Conflicting records?</strong> Remove any old{" "}
                {useCname ? "CNAME or A" : "A"} records for @ and www that point
                to different values.
              </Typography>
            </li>
          </Box>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} variant="contained">
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Reusable DNS record display component
 */
const DnsRecordDisplay = ({ type, host, value, ttl, onCopy }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.5,
      display: "flex",
      gap: 2,
      alignItems: "center",
      flexWrap: "wrap",
      bgcolor: "background.default",
    }}
  >
    <Box>
      <Typography variant="caption" color="text.secondary">
        Type
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {type}
      </Typography>
    </Box>
    <Box>
      <Typography variant="caption" color="text.secondary">
        Host / Name
      </Typography>
      <Typography variant="body2" fontFamily="monospace">
        {host}
      </Typography>
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography variant="caption" color="text.secondary">
        Value / Points to
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography variant="body2" fontFamily="monospace">
          {value}
        </Typography>
        {value && onCopy && (
          <Tooltip title="Copy value">
            <IconButton size="small" onClick={() => onCopy(value)}>
              <ContentCopy sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
    <Box>
      <Typography variant="caption" color="text.secondary">
        TTL
      </Typography>
      <Typography variant="body2">{ttl}</Typography>
    </Box>
  </Paper>
);

export default DnsSetupGuideDialog;
